import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";
import DropdownField from "../../components/Editing/DropdownField";
import { getActionEnumOptions } from "../../utils/pdApiUtils";

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};

interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const baseEnumFields = nameofFactory<Api.BaseEnumeration>();

const EditEnumCommon: React.FC<OwnProps> = ({ toggleModal, dataFieldName }) => {
  const dispatch = useDispatch();

  const editedEnumeration = useSelector((state: State.Root) => state.editView.editedObject);
  const storedEnumeration = useSelector((state: State.Root) => state.editView.storedObject);
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const activeUser = useSelector((state: State.Root) => state.main.activeUser);

  const isNew = !storedEnumeration;
  const permission = hasWriteAccess(
    activeUser,
    dataFieldName === "electrifications" ? undefined : "Escalator"
  );

  let hasLinkedDomains = false;

  if (dataFieldName === "electrifications" && !isNew) {
    const selectedElectrification = pdApi.electrifications?.find(
      electrification => electrification.enumValue === editedEnumeration.enumValue
    );
    hasLinkedDomains = (selectedElectrification?.domains?.length ?? 0) > 0;
  }

  let eventClassOptions : ReturnType<typeof getActionEnumOptions> = [];
  if (dataFieldName === "EscalatorEventTypes" && pdApi.EscalatorEventClasses) {
    eventClassOptions = getActionEnumOptions(pdApi.EscalatorEventClasses);
    eventClassOptions.push({ key: -1, text: "None", value: -1 });
  }

  const isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  const checkSpecialCharacters = (str: string): boolean =>
    !/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_]+$/.test(str);

  const isFieldValid = (field: string): ErrorStatus => {
    const isNew = !storedEnumeration;
    switch (field) {
      case baseEnumFields("enumName"): {
        const { enumName } = editedEnumeration;

        if (isEmptyString(enumName)) {
          return { errorStatus: true, errorMsg: "The enumeration name cannot be empty." };
        } else if (checkSpecialCharacters(enumName) && dataFieldName !== "electrifications") {
          return {
            errorStatus: true,
            errorMsg: "The enumeration name must contain only alphabets numbers and underscores."
          };
        } else {
          return _.values(pdApi[dataFieldName]).find(
            enumeration =>
              (isNew || enumeration.enumValue !== editedEnumeration.enumValue) &&
              enumeration.enumName.trim().toLowerCase() ===
                editedEnumeration.enumName.trim().toLowerCase()
          )
            ? { errorStatus: true, errorMsg: "The enumeration name must be unique." }
            : { errorStatus: false, errorMsg: "" };
        }
      }

      case baseEnumFields("enumValue"):
        if (storedEnumeration) {
          return { errorStatus: false, errorMsg: "" };
        }
        return _.values(pdApi[dataFieldName]).find(
          enumeration => enumeration.enumValue === editedEnumeration.enumValue
        )
          ? { errorStatus: true, errorMsg: "The value must be unique." }
          : { errorStatus: false, errorMsg: "" };

      default:
        return { errorStatus: false, errorMsg: "" };
    }
  };

  const isAllFieldsValid = (): boolean => {
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };

  const errorenumName = isFieldValid(baseEnumFields("enumName"));
  const errorenumValue = isFieldValid(baseEnumFields("enumValue"));

  if (storedEnumeration) {
    storedEnumeration.comment = storedEnumeration.comment ? storedEnumeration.comment : "";
  }

  return (
    <Editor
      itemName={dataFieldName === "electrifications" ? "Electrification" : "Enumeration"}
      originalObject={storedEnumeration}
      editedObject={editedEnumeration}
      hasWritePermission={permission}
      isValid={isAllFieldsValid()}
      cancel={() => dispatch(editActions.cancelEditing())}
      update={() => dispatch(editActions.update(dataFieldName as Api.OptionsGrud))}
      create={() => dispatch(editActions.add(dataFieldName as Api.OptionsGrud))}
      remove={{
        callback: toggleModal,
        disabled: hasLinkedDomains,
        disabledTooltip: ": Item linked to child components"
      }}
    >
      <NumberField
        id="enumValue"
        label="Value"
        value={editedEnumeration.enumValue}
        isError={errorenumValue.errorStatus}
        errorMessage={errorenumValue.errorMsg}
        readonly={!isNew}
        onChange={enumValue =>
          dispatch(editActions.setEdited({ ...editedEnumeration, enumValue: enumValue }))
        }
      />
      <TextField
        id="enumName"
        label="Enumeration Name"
        value={editedEnumeration.enumName}
        isError={errorenumName.errorStatus}
        errorMessage={errorenumName.errorMsg}
        readonly={dataFieldName === "electrifications" && !isNew ? true : !permission}
        onChange={enumName =>
          dispatch(editActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() }))
        }
      />
      <TextField
        id="comment"
        label="Comment"
        value={editedEnumeration.comment}
        isError={!isFieldValid(baseEnumFields("comment"))}
        readonly={!permission}
        onChange={comment =>
          dispatch(editActions.setEdited({ ...editedEnumeration, comment: comment.trim() }))
        }
      />
      {dataFieldName === "EscalatorEventTypes" && (
        <DropdownField
          id="doorEventClassId"
          label="Event Class"
          value={editedEnumeration.escalatorEventClassId === null ? -1 : editedEnumeration.escalatorEventClassId}
          options={eventClassOptions}
          search={true}
          onChange={escalatorEventClassId => {
            escalatorEventClassId  =  escalatorEventClassId === -1 ? null : escalatorEventClassId;
            dispatch(editActions.setEdited({ ...editedEnumeration, escalatorEventClassId }));
          }}
          disabled={!permission}
        />
      )}
    </Editor>
  );
};

export default EditEnumCommon;
